import OutlineButton from "@components/Buttons/OutlineButton";
import TableWithPagination from "@components/Tables/TableWithPagination";
import { useTheme } from "@hooks/useTheme";
import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";

const toDisplayList = (list) => {
  if (!Array.isArray(list)) return [];
  return list.map((item, idx) => {
    // Handle object with componentName, componentVersion, licenseName
    if (item && typeof item === "object") {
      const componentName = item.componentName || item.name || "";
      const componentVersion = item.componentVersion || item.version || "";
      const licenseName = item.licenseName || item.license || item.componentLicense || "---";

      return {
        order: idx + 1,
        component: componentName,
        componentName,
        componentVersion,
        licenseName,
        componentLicense: licenseName,
      };
    }

    // Handle string format like "techcon-files-client    [ 0.0.1 ]    [ N/A ]"
    if (typeof item === "string") {
      // Pattern: componentName    [ version ]    [ license ]
      const pattern = /^(.+?)\s+\[\s*(.+?)\s*\]\s+\[\s*(.+?)\s*\]$/;
      const match = item.match(pattern);
      
      if (match) {
        const componentName = match[1].trim();
        const componentVersion = match[2].trim();
        const rawLicense = match[3].trim();

        const licenseName =
          !rawLicense ||
          rawLicense.toLowerCase() === "null" ||
          rawLicense.toUpperCase() === "N/A"
            ? "---"
            : rawLicense;
        
        return {
          order: idx + 1,
          component: componentName,
          componentName,
          componentVersion,
          licenseName,
          componentLicense: licenseName,
        };
      }
      
      // Fallback: Handle string format like "componentName (version)"
      const fallbackMatch = item.match(/^(.+?)\s*\((.+?)\)$/);
      if (fallbackMatch) {
        return {
          order: idx + 1,
          component: fallbackMatch[1].trim(),
          componentName: fallbackMatch[1].trim(),
          componentVersion: fallbackMatch[2].trim(),
          licenseName: "---",
          componentLicense: "---",
        };
      }
      
      // Simple string without version/license
      return {
        order: idx + 1,
        component: item.trim(),
        componentName: item.trim(),
        componentVersion: "",
        licenseName: "---",
        componentLicense: "---",
      };
    }

    return {
      order: idx + 1,
      component: String(item),
      componentName: String(item),
      componentVersion: "",
      licenseName: "---",
      componentLicense: "---",
    };
  });
};

const SbomItemsList = (props) => {
  const { t } = useTranslation();
  const {
    title = t("project.sbom.item-list-title"),
    missings = [],
    notApplicables = [],
    onDownload,
    itemsPerPage = 10,
    isDisplayDownloadButton = true,
    firstColumnTitle = "",
    secondColumnTitle = "",
  } = props;
  const { colors } = useTheme();

  const missingRows = useMemo(() => toDisplayList(missings), [missings]);
  const notApplicableRows = useMemo(() => toDisplayList(notApplicables), [notApplicables]);

  const [missingPage, setMissingPage] = useState(1);
  const [naPage, setNaPage] = useState(1);

  const missingTotalPages = Math.max(1, Math.ceil(missingRows.length / itemsPerPage));
  const naTotalPages = Math.max(1, Math.ceil(notApplicableRows.length / itemsPerPage));

  // Ensure current page stays within bounds when data changes
  useEffect(() => {
    if (missingPage > missingTotalPages) setMissingPage(1);
  }, [missingTotalPages, missingPage]);
  useEffect(() => {
    if (naPage > naTotalPages) setNaPage(1);
  }, [naTotalPages, naPage]);

  const sliceByPage = (rows, page) => rows.slice((page - 1) * itemsPerPage, page * itemsPerPage);
  const pagedMissingRows = useMemo(
    () => sliceByPage(missingRows, missingPage),
    [missingRows, missingPage, itemsPerPage],
  );
  const pagedNotApplicableRows = useMemo(
    () => sliceByPage(notApplicableRows, naPage),
    [notApplicableRows, naPage, itemsPerPage],
  );

  const columnConfig = {
    component: {
      title: "",
      customRender: (value, row) => {
        const componentVersion = row.componentVersion || "";
        const licenseIdentifier = row.componentLicense || row.licenseName || "";
        const isVersionLong = componentVersion.length > 15; // Threshold for truncation

        return (
          <div>
            <div className={`text-sm font-medium ${colors.textNeutralLight}`}>
              <span className={`${colors.textPrimary}`}>{row.component || row.componentName}</span>
              {componentVersion && (
                <span className={colors.textNeutralMuted}>
                  {` (`}
                  {isVersionLong ? (
                    <span className="truncate max-w-[120px] inline-block align-bottom" title={componentVersion}>
                      {componentVersion}
                    </span>
                  ) : (
                    componentVersion
                  )}
                  {`)`}
                </span>
              )}
            </div>
            <div className={`mt-0.5 text-xs ${colors.textNeutralMuted}`}>
              {licenseIdentifier || ""}
            </div>
          </div>
        );
      },
      style: { whiteSpace: "normal" },
    },
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <h2 className={`text-md font-bold ${colors.textPrimary}`}>{title}</h2>
        {isDisplayDownloadButton && onDownload && (missingRows.length > 0 || notApplicableRows.length > 0) && (
          <OutlineButton size="md" onClick={onDownload} className="mr-5">
            {t("common.download-sbom-verification-result", "Download SBOM Verif. Result")}
          </OutlineButton>
        )}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Missing List */}
        <div className={`rounded-2xl ${colors.bgPrimary} p-4`}>
          <TableWithPagination
            columns={["component"]}
            columnConfig={{
              component: {
                ...columnConfig.component,
                headerRender: () => (
                  <div className={`text-sm ${colors.textPrimary} font-medium uppercase`}>{firstColumnTitle}</div>
                ),
              },
            }}
            data={pagedMissingRows}
            loading={false}
            totalPages={missingTotalPages}
            totalData={missingRows.length}
            currentPage={missingPage}
            setCurrentPage={setMissingPage}
            maxRowsPerPage={itemsPerPage}
            hasCheckbox={false}
            hasRadioButton={false}
            rowKey="order"
            shouldShowGoToPage={true}
            containerStyle={{ borderRadius: "12px" }}
          />
        </div>

        {/* Not Applicable List */}
        <div className={`rounded-2xl ${colors.bgPrimary} p-4`}>
          <TableWithPagination
            columns={["component"]}
            columnConfig={{
              component: {
                ...columnConfig.component,
                headerRender: () => (
                  <div className={`text-sm ${colors.textPrimary} font-medium uppercase`}>{secondColumnTitle}</div>
                ),
              },
            }}
            data={pagedNotApplicableRows}
            loading={false}
            totalPages={naTotalPages}
            totalData={notApplicableRows.length}
            currentPage={naPage}
            setCurrentPage={setNaPage}
            maxRowsPerPage={itemsPerPage}
            hasCheckbox={false}
            hasRadioButton={false}
            rowKey="order"
            shouldShowGoToPage={true}
            containerStyle={{ borderRadius: "12px" }}
          />
        </div>
      </div>
    </div>
  );
};

export default SbomItemsList;
